
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, GraduationCap, Users, ShieldCheck, MessageCircle, ArrowRight, BrainCircuit } from 'lucide-react';
import { WHATSAPP_NUMBER } from '../constants';

const Home: React.FC = () => {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative bg-slate-900 overflow-hidden py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://picsum.photos/seed/education-success/1920/1080" 
            alt="Education Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center lg:text-left">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-in fade-in slide-in-from-left duration-700">
              <span className="inline-block bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full text-sm font-bold tracking-wide uppercase mb-6 border border-blue-400/30">
                Success Starts Here
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
                Master Your Board Exams with <span className="text-blue-500">EduZone</span>
              </h1>
              <p className="text-lg text-slate-300 mb-10 max-w-2xl leading-relaxed">
                EduZone is an educational platform designed to help students of Class 9, Class 10, and Intermediate prepare effectively for their exams. Here you can find clear notes, important questions, MCQs, and exam preparation resources based on the Pakistan board syllabus, including BISE Lahore and all other boards. Our goal is to make learning simple and help students understand their subjects better and perform confidently in exams.
              </p>
              <div className="relative max-w-xl mb-10 mx-auto lg:mx-0">
                <div className="flex items-center bg-white rounded-2xl p-1 shadow-xl border border-white/20">
                  <div className="pl-4 text-slate-400">
                    <Search className="h-5 w-5" />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Search for notes (e.g. Physics Class 9)..." 
                    className="w-full px-4 py-3 bg-transparent text-slate-900 focus:outline-none font-medium"
                  />
                  <button className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition">
                    Search
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/notes-hub" className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold hover:bg-blue-700 transition shadow-xl flex items-center justify-center">
                  Explore Notes Hub <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/exam-prep" className="bg-white/10 backdrop-blur text-white border border-white/20 px-10 py-4 rounded-xl font-bold hover:bg-white/20 transition flex items-center justify-center">
                  Study Tips
                </Link>
              </div>
            </div>
            <div className="hidden lg:block animate-in zoom-in duration-1000">
              <img 
                src="https://images.unsplash.com/photo-1637589316488-6d4c41b335cd?q=80&w=1031&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                alt="EduZone Student" 
                className="rounded-3xl shadow-2xl border-4 border-slate-800 w-full object-cover aspect-[4/3]"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 tracking-tight">Pakistan's Authority for All Board Education</h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              EduZone is more than just a notes website. We are a dedicated academic support system designed specifically for the needs of students under the Punjab Curriculum and Textbook Board (PCTB). Our mission is to bridge the educational gap by providing premium resources at zero cost.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-blue-50 transition">
              <div className="bg-blue-600 w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                <Search className="text-white w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Easy Accessibility</h3>
              <p className="text-slate-600">Find subject-wise and chapter-wise notes in seconds with our optimized navigation system.</p>
            </div>
            <div className="p-8 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-blue-50 transition">
              <div className="bg-blue-600 w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                <ShieldCheck className="text-white w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Verified Quality</h3>
              <p className="text-slate-600">Our sample notes are drafted by experienced teachers who understand the BISE Lahore paper pattern.</p>
            </div>
            <div className="p-8 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-blue-50 transition">
              <div className="bg-blue-600 w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                <BrainCircuit className="text-white w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Smart Prep</h3>
              <p className="text-slate-600">Combine our notes with expert study tips to maximize your scores in the upcoming board exams.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Class Navigation */}
      <section className="py-20 bg-slate-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-12">Select Your Academic Level</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Class 9", path: "/class/class-9", desc: "Matric Part I" },
              { title: "Class 10", path: "/class/class-10", desc: "Matric Part II" },
              { title: "1st Year", path: "/class/class-11", desc: "Intermediate Part I" },
              { title: "2nd Year", path: "/class/class-12", desc: "Intermediate Part II" }
            ].map((item, idx) => (
              <Link key={idx} to={item.path} className="group bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition border border-gray-100">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 group-hover:text-white transition">
                  <GraduationCap className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 mb-6">{item.desc}</p>
                <div className="text-blue-600 font-bold inline-flex items-center text-sm uppercase tracking-wider group-hover:underline">
                  View Notes <ArrowRight className="ml-1 w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Support */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center lg:text-left">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div>
              <h2 className="text-3xl font-bold mb-4">Join Our WhatsApp Student Community</h2>
              <p className="text-green-50 text-lg opacity-90 max-w-xl">Get instant updates about new notes, board notifications, and date sheets directly on your phone.</p>
            </div>
            <a 
              href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\s+/g, '')}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-green-600 px-10 py-5 rounded-full font-bold text-lg hover:bg-green-50 transition shadow-2xl flex items-center"
            >
              <MessageCircle className="mr-3 h-6 w-6" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* How to Get Notes Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <img 
                src="https://plus.unsplash.com/premium_photo-1723478495487-c436ae82f48f?q=80&w=652&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                alt="EduZone Student" 
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]"
                referrerPolicy="no-referrer"
              />
              <div className="absolute -bottom-10 -right-10 bg-blue-600 p-8 rounded-2xl shadow-2xl text-white hidden md:block">
                <p className="text-4xl font-bold">100%</p>
                <p className="text-sm font-medium">Free Access Always</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 leading-tight">How to Get Your Notes Easily</h2>
              <div className="space-y-8">
                {[
                  { step: "01", title: "Select Your Class", desc: "Choose your grade from the main menu or home page categories." },
                  { step: "02", title: "Pick a Subject", desc: "Click on the subject you need notes for, like Physics or Mathematics." },
                  { step: "03", title: "Review Sample", desc: "Check the sample chapters provided to ensure they meet your needs." },
                  { step: "04", title: "Contact on WhatsApp", desc: "Click the 'Contact at WhatsApp' button to get your notes instantly." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-6">
                    <span className="text-4xl font-black text-blue-100 flex-shrink-0 leading-none">{item.step}</span>
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
